import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { employerAPI, getProxiedLogoUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';

const DEFAULT_FORM = {
  companyName: '',
  companyDescription: '',
  companyWebsite: '',
  companySize: '',
  industry: '',
  headquartersLocation: '',
  remoteWorkPolicy: '',
  contactEmail: '',
  contactPhone: '',
  linkedinUrl: '',
};

const EditEmployerProfile = ({ onClose, onSuccess }) => {
  const { refreshProfile } = useAuth();
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const apiBaseUrl = useMemo(
    () => (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, ''),
    []
  );

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await employerAPI.getProfile();
        const profile = response.data || {};
        
        // Handle both camelCase and snake_case from API
        const getValue = (camelKey, snakeKey) => {
          return profile[camelKey] || profile[snakeKey] || '';
        };
        
        setFormData({
          companyName: getValue('companyName', 'company_name'),
          companyDescription: getValue('companyDescription', 'company_description'),
          companyWebsite: getValue('companyWebsite', 'company_website'),
          companySize: getValue('companySize', 'company_size'),
          industry: profile.industry || '',
          headquartersLocation: getValue('headquartersLocation', 'headquarters_location'),
          remoteWorkPolicy: getValue('remoteWorkPolicy', 'remote_work_policy'),
          contactEmail: getValue('contactEmail', 'contact_email'),
          contactPhone: getValue('contactPhone', 'contact_phone'),
          linkedinUrl: getValue('linkedinUrl', 'linkedin_url'),
        });

        // Handle logo URL (both formats)
        const logoUrl = profile.companyLogoUrl || profile.company_logo_url;
        if (logoUrl) {
          // Use the proxied logo URL utility if it's an S3 URL
          const proxiedUrl = getProxiedLogoUrl(logoUrl, apiBaseUrl);
          setLogoPreview(proxiedUrl || logoUrl);
        }
      } catch (error) {
        console.error('Error fetching employer profile:', error);
        toast.error('Failed to load employer profile.');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [apiBaseUrl, onClose]);

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (PNG, JPG, SVG, WEBP, GIF).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo size must be less than 5MB.');
      return;
    }

    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const buildPayload = () => {
    const payload = {};
    if (formData.companyName !== undefined) payload.company_name = formData.companyName || null;
    if (formData.companyDescription !== undefined) payload.company_description = formData.companyDescription || null;
    if (formData.companyWebsite !== undefined) payload.company_website = formData.companyWebsite || null;
    if (formData.companySize !== undefined) payload.company_size = formData.companySize || null;
    if (formData.industry !== undefined) payload.industry = formData.industry || null;
    if (formData.headquartersLocation !== undefined) payload.headquarters_location = formData.headquartersLocation || null;
    if (formData.remoteWorkPolicy !== undefined) payload.remote_work_policy = formData.remoteWorkPolicy || null;
    if (formData.contactEmail !== undefined) payload.contact_email = formData.contactEmail || null;
    if (formData.contactPhone !== undefined) payload.contact_phone = formData.contactPhone || null;
    if (formData.linkedinUrl !== undefined) payload.linkedin_url = formData.linkedinUrl || null;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = buildPayload();
      await employerAPI.updateProfile(payload);

      let updatedLogoUrl = null;
      if (logoFile) {
        try {
          setUploadingLogo(true);
          const { data: logoResponse } = await employerAPI.uploadLogo(logoFile);
          updatedLogoUrl = logoResponse.companyLogoUrl;
        } catch (error) {
          console.error('Error uploading company logo:', error);
          toast.error('Profile updated, but failed to upload company logo.');
        } finally {
          setUploadingLogo(false);
        }
      }

      const refreshedUser = await refreshProfile();
      if (updatedLogoUrl && refreshedUser) {
        refreshedUser.companyLogoUrl = updatedLogoUrl;
        localStorage.setItem('user', JSON.stringify(refreshedUser));
      }

      toast.success('Employer profile updated successfully.');
      if (onSuccess) {
        onSuccess({
          ...formData,
          companyLogoUrl: updatedLogoUrl || (refreshedUser?.companyLogoUrl ?? null),
        });
      }
      onClose();
    } catch (error) {
      console.error('Error updating employer profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update employer profile.');
    } finally {
      setSubmitting(false);
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 shadow-lg flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading employer profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Company Profile</h2>
            <p className="text-sm text-gray-500 mt-1">
              Update your company details and branding assets.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close profile editor"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className="px-6 py-6 space-y-6" onSubmit={handleSubmit}>
          <section className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Branding</h3>
            <div className="flex flex-col md:flex-row gap-6 md:items-center">
              <div className="w-32 h-32 bg-white border border-dashed border-gray-300 rounded-xl flex items-center justify-center overflow-hidden">
                {logoPreview ? (
                  <img src={logoPreview} alt="Company logo preview" className="w-full h-full object-contain" />
                ) : (
                  <div className="text-center text-gray-400 text-sm">
                    <span className="block text-2xl mb-2">🏢</span>
                    Company Logo
                  </div>
                )}
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Logo</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp,image/gif"
                  onChange={handleLogoChange}
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4
                             file:rounded-md file:border-0 file:text-sm file:font-semibold
                             file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Supported formats: PNG, JPG, SVG, WEBP, GIF. Maximum size 5MB.
                </p>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Website</label>
              <input
                type="url"
                name="companyWebsite"
                value={formData.companyWebsite}
                onChange={handleInputChange}
                placeholder="https://"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
              <input
                type="text"
                name="companySize"
                value={formData.companySize}
                onChange={handleInputChange}
                placeholder="e.g. 50-100 employees"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <input
                type="text"
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                placeholder="e.g. Technology"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Headquarters Location</label>
              <input
                type="text"
                name="headquartersLocation"
                value={formData.headquartersLocation}
                onChange={handleInputChange}
                placeholder="City, Country"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remote Work Policy</label>
              <input
                type="text"
                name="remoteWorkPolicy"
                value={formData.remoteWorkPolicy}
                onChange={handleInputChange}
                placeholder="e.g. Hybrid / Remote-first"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </section>

          <section>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Description</label>
            <textarea
              name="companyDescription"
              rows="4"
              value={formData.companyDescription}
              onChange={handleInputChange}
              placeholder="Tell candidates about your mission, culture, and what makes your organisation unique."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <input
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
              <input
                type="url"
                name="linkedinUrl"
                value={formData.linkedinUrl}
                onChange={handleInputChange}
                placeholder="https://www.linkedin.com/company/your-company"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </section>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || uploadingLogo}
              className="px-6 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : uploadingLogo ? 'Uploading Logo...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployerProfile;

